import { defineComponent as _defineComponent } from 'vue'
import type { DataInstance } from '@busy-human/gearbox';
import { daysInMonth, Dogspaces, Events, weekOfDay } from '@busy-human/k9-library';
import { computed, ref } from 'vue';
import ArrowButtonSVG from '../resources/arrow-button.svg';
import VisibilityOffIcon from '../resources/icons/visibility_off.svg';
import VisibilityOnIcon from '../resources/icons/visibility_on.svg';
import { useCalendars } from '@/store/useCalendars';
import { useDogspace } from '@/store/dogspaces';
import { magicNumbers as consts, commonStrings } from '@/util/constants';
import {fns} from "@/util/linear-calendar";
import EventToolTip from "./sub-component/event-tooltip.vue";
import { useTrainers } from '@/store/useTrainers';

/* eslint-disable max-depth */

type Props = {
	eventList: DataInstance<Events.Model>[]
	display?: 'week' | 'month' | 'day'
	selectedDate?: Date
	sortCategory?: 'trainer' | 'kennel' | 'time'
}

type Emits = {
	(e: 'selectEvent', eventId: string): void
	(e: 'update:selectedDate', date: Props["selectedDate"]): void
}

type AugmentedEvent = {
	event: Events.Model
	startColumn: number
	endColumn: number
	startsBefore: boolean
	endsAfter: boolean
	backgroundColor?: string
	foregroundColor?: string
}

const SHOW_HIDDEN_ROWS = true;

// -- user intraction

export default /*@__PURE__*/_defineComponent({
  __name: 'linear-calendar',
  props: {
    eventList: {},
    display: { default: 'month' },
    selectedDate: { default: () => new Date() },
    sortCategory: { default: 'time' }
  },
  emits: ["selectEvent", "update:selectedDate"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const DAYS:{[key:string]:string} = {
	"1": "day",
	"7": "week",
	"28": "month-28",
	"29": "month-29",
	"30": "month-30",
	"31": "month-31",
};

const props = __props;
const emit = __emit;

const toolTipEvent = ref<null | AugmentedEvent>(null);
// -- Group vis
const groupVis = ref<Record<string, boolean>>({});

// -- Stores
const calendars = useCalendars();
const trainers = useTrainers();
const dogs = useDogspace();

// -- Constants
const WEEKLENGTH = consts.ms.in.week;
const Months = commonStrings.months;
const adjustTimeframe = (delta = 1) => {
	let tmp: Date;
	if(props.display === 'month') {
		tmp = new Date(props.selectedDate);
		tmp.setMonth(tmp.getMonth() + delta);
	} else if(props.display === 'week') {
		tmp = new Date(props.selectedDate.getTime() + (WEEKLENGTH * delta));
	} else {
		tmp = new Date(props.selectedDate);
		tmp.setDate(tmp.getDate() + delta);
	}
	emit('update:selectedDate', tmp);
};

function setTooltip(event: AugmentedEvent | null){
	// console.info(" SET TOOL TIP CALLED", event);
	if(event){
		toolTipEvent.value = event;
	}else{
		toolTipEvent.value = null;
	}
}

const dogDetails = (evt: AugmentedEvent) => (
	'dogId' in evt.event ? dogs.dogs[evt.event.dogId as string] : null
);

function transformDateDay(i: number) {
	return fns.transformDateDay(i, props);
}
function transformDateNumber(i: number) {
	return fns.transformDateNumber(i, props);
}
function buildEventCSS(ev: AugmentedEvent){
	return fns.buildEventCSS(ev);
}
function isAllDayEvent(event: Events.Model){
	return fns.isAllDayEvent(event);
}

// -- Computed
/** Number of days in this selection */
const daysInSelection = computed(() => {
	if(props.display === 'month') {
		return daysInMonth(props.selectedDate.getFullYear(), props.selectedDate.getMonth());
	} else if(props.display === 'week') {
		return consts.day.in.week;
	} else {
		return 1;
	}
});

const columnCount = computed(()=>{
	const count = DAYS[daysInSelection.value.toString()];
	return count;
});

/** Groups of events */
// eslint-disable-next-line complexity
const eventGroups = computed(() => {
	/** A grouping of events */
	type Group = {
		title: string
		events: AugmentedEvent[]
		color?: string
	}
	const groups: Group[] = [];
	/** Cache group */
	const timeframe: `${number}-${number}` = `${props.selectedDate.getFullYear()}-${props.selectedDate.getMonth()}`;

	const calEvs = calendars.events.get(timeframe);
	if(typeof calEvs === 'undefined') {
		// Fetch Calendar events, if we have not fetched them already
		calendars.fetchEvents(props.selectedDate.getFullYear(), props.selectedDate.getMonth());
	}

	// Group by kennel
	if(props.sortCategory === 'kennel') {
		if(calEvs) {
			const evMap = new Map<Dogspaces.Model['size'], AugmentedEvent[]>();

			for(const events of Object.values(calEvs)) {
				const evs = fns.processEvents(props, events, trainers.trainers, daysInSelection);
				for(const ev of evs) {
					if(!Events.isAppEvent(ev.event)) continue;
					const dog = dogs.dogs[ev.event.dogId];
					if(!dog) continue;

					if(!evMap.has(dog.size)) evMap.set(dog.size, []);
					evMap.get(dog.size)?.push(ev);
				}
			}

			for(const [size, events] of evMap) {
				groups.push({
					title: size.substring(0, 1).toUpperCase(),
					events
				});
			}
			groups.sort((a, b) => b.title.localeCompare(a.title)); // At least sort them so the order is consistent
		}

	// Group by Trainer
	} else if(props.sortCategory === 'trainer') {
		if(calEvs) {
			// Loop through all calendars
			for(const [trainerId, events] of Object.entries(calEvs)) {
				if(events.length > 0) {
					// Process events, push to groups
					const trainer = trainers.trainers.get(trainerId);

					groups.push({
						title: trainer?.nickname || (trainerId === "Unassigned" || trainerId === 'Main' ? trainerId : `Unknown (${trainerId})`),
						events: fns.processEvents(props, events, trainers.trainers, daysInSelection),
						color: trainer?.calendarColor
					});
				}
			}
		}

	// Group by Time (hour/half hour)
	} else {
		// Create groups preemptively
		for(let i = 0; i < consts.hour.in.day ; i+=0.5) {
			const isHalfHour = Math.floor(i) !== i;
			const tmp = new Date();
			tmp.setHours(i);
			tmp.setMinutes(!isHalfHour ? 0 : consts.minutes.in.halfHour);
			const title = tmp.toLocaleTimeString("default", {'hour12': true, hour: 'numeric', minute: 'numeric'});
			groups.push({title, events: []});
		}

		if(calEvs) {
			// Loop through calendars
			for(const [trainerId, events] of Object.entries(calEvs)) {
				// Process events, then group by hour/half hour
				const evs = fns.processEvents(props, events, trainers.trainers, daysInSelection);
				for(const ev of evs) {
					if(fns.isAllDayEvent(ev.event)) continue;
					const tmp = new Date(ev.event.startTime);
					const isHalfHour = tmp.getMinutes() >= consts.minutes.in.halfHour;
					const hour = tmp.getHours();
					const group = (hour * 2) + (isHalfHour ? 1 : 0);
					groups[group].events.push(ev);
				}
			}
		}
	}
	return groups;
});


const __returned__ = { DAYS, props, emit, toolTipEvent, groupVis, calendars, trainers, dogs, WEEKLENGTH, Months, SHOW_HIDDEN_ROWS, adjustTimeframe, setTooltip, dogDetails, transformDateDay, transformDateNumber, buildEventCSS, isAllDayEvent, daysInSelection, columnCount, eventGroups, get daysInMonth() { return daysInMonth }, get Dogspaces() { return Dogspaces }, get Events() { return Events }, get weekOfDay() { return weekOfDay }, computed, ref, get ArrowButtonSVG() { return ArrowButtonSVG }, get VisibilityOffIcon() { return VisibilityOffIcon }, get VisibilityOnIcon() { return VisibilityOnIcon }, get useCalendars() { return useCalendars }, get useDogspace() { return useDogspace }, get consts() { return consts }, get commonStrings() { return commonStrings }, get fns() { return fns }, EventToolTip, get useTrainers() { return useTrainers } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})